import { useState, type ReactNode } from 'react';
import { Popover } from '@base-ui/react/popover';
import './KpiTile.css';

/**
 * STITCH-2 KPI tile primitive (design-language 03 P1; DESIGN DIRECTION law
 * (h)): uppercase micro label, big tabular-nums value, optional plain sub
 * line, severity tone rendered as a tinted surface + 3px inset edge (color
 * never the sole carrier -- the sub line names the state). Interactive tiles
 * (onClick given) are BUTTONS with aria-pressed and a hover lift; static
 * tiles render as divs and never lift.
 */
export type KpiTileTone = 'ok' | 'warm' | 'breach' | 'revenue' | 'coming';

/** RECEPTION-FIX-AND-POLISH-BATCH-V1 (UX LAW 2 - numbers first): the shell's
 *  BIG-NUMBER grade. 'hero' renders the value at --ac-text-metric-lg for the
 *  day's headline figures (money tiles, summary strips); 'base' stays the
 *  operational grade every existing tile already uses. An UNPROVEN figure
 *  renders value '-' with the sub naming the truth ("Not derived yet") --
 *  never a fake zero (DENSITY rule 6). */
export type KpiTileSize = 'base' | 'hero';

export interface KpiTileProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly sub?: ReactNode;
  readonly tone?: KpiTileTone;
  readonly size?: KpiTileSize;
  readonly active?: boolean;
  readonly onClick?: () => void;
  /** Plain-language help, available through the separate information button. */
  readonly hint?: string;
  readonly testId?: string;
}

export function kpiTileClass(
  tone: KpiTileTone = 'ok',
  size: KpiTileSize = 'base',
): string {
  const toneClass = tone === 'ok' ? 'ac-tile' : `ac-tile ac-tile--${tone}`;
  return size === 'hero' ? `${toneClass} ac-tile--hero` : toneClass;
}

export default function KpiTile({
  label,
  value,
  sub,
  tone = 'ok',
  size = 'base',
  active,
  onClick,
  hint,
  testId,
}: KpiTileProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  const hasHint = Boolean(hint?.trim());
  const body = (
    <>
      {!hasHint && <span className="ac-tile__label">{label}</span>}
      <span className="ac-tile__value">{value}</span>
      {sub !== undefined && sub !== null ? (
        <span className="ac-tile__sub">{sub}</span>
      ) : null}
    </>
  );
  if (hasHint) {
    return (
      <div className={`${kpiTileClass(tone, size)} ac-tile-help-host`} data-testid={onClick ? undefined : testId}>
        <Popover.Root open={helpOpen} onOpenChange={setHelpOpen}>
          <div className="ac-tile-help-label-row">
            <span className="ac-tile__label">{label}</span>
          <Popover.Trigger className="ac-tile-help-trigger" aria-label={`About ${label}`}
            openOnHover delay={150} closeDelay={150}
            onFocus={(event) => {
              if (event.currentTarget.matches(':focus-visible') &&
                  !(event.relatedTarget instanceof Element && event.relatedTarget.closest('.ac-tile-help-popup'))) {
                setHelpOpen(true);
              }
            }}>
            <span aria-hidden="true">i</span>
          </Popover.Trigger>
          </div>
          <Popover.Portal>
            <Popover.Positioner side="top" align="start" sideOffset={8} collisionPadding={8}
              className="ac-tile-help-positioner">
              <Popover.Popup className="ac-tile-help-popup" initialFocus={false}>
                <Popover.Title className="ac-tile-help-title">About {label}</Popover.Title>
                <Popover.Description className="ac-tile-help-description">{hint}</Popover.Description>
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
        {onClick ? (
          <button type="button" className="ac-tile-help-action" onClick={onClick}
            aria-label={label} aria-pressed={active === true} data-testid={testId}>{body}</button>
        ) : body}
      </div>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        className={kpiTileClass(tone, size)}
        onClick={onClick}
        aria-pressed={active === true}
        title={hint}
        data-testid={testId}
      >
        {body}
      </button>
    );
  }
  return (
    <div className={kpiTileClass(tone, size)} title={hint} data-testid={testId}>
      {body}
    </div>
  );
}
